import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { isEqual, debounce, find } from 'lodash';
import { isLoading } from 'lib/redux-utils';
import Api from 'services/Api';
import Auth from 'modules/Auth';
import Fieldset from 'components/Fieldset';
import withLazyStyle from 'components/LazyStyle';
import dropdownStyle from 'components/Dropdown/dropdown.css?lazy';
import AddressList from './AddressList';
import ManualAddress from './ManualAddress';
import style from './addressPicker.css?lazy';

const SEARCH_DEBOUNCE_MS = 120;
const fromElement = (elem) => ({ target: elem });
const loqateLabel = (loqateAddress) =>
  loqateAddress && loqateAddress.label && loqateAddress.label.replace(/\n/g, ', ');

class _AddressPicker extends Component {
  constructor(props) {
    super(props);
    this.elemRef = React.createRef();
    this.wrapperRef = React.createRef();
    const address = {
      postcode: props.postal.value,
      country: props.country.value,
      city: props.city.value,
      line1: props.address.value,
      line2: props.address2.value
    };
    this.state = {
      addresses: [],
      // loqateAddress state will be lost on step back/forward if manual was selected as we are only
      // tracking either manual or loqate address. A consequence of re-using the
      // `homeAddress` field.
      loqateAddress: props.isManualAddress ? null : address,
      manualAddress: {
        type: 'manual',
        ...address
      },
      serverError: null,
      focusSearch: false,
      isExpanded: false
    };
  }

  static getDerivedStateFromProps(props, state) {
    const newExpectedManualAddress = {
      type: 'manual',
      postcode: props.postal.value,
      country: props.country.value,
      city: props.city.value,
      line1: props.address.value,
      line2: props.address2.value
    };
    if (props.isManualAddress && !isEqual(state.manualAddress, newExpectedManualAddress)) {
      return {
        manualAddress: newExpectedManualAddress
      };
    }
    if (!props.query.value) {
      return {
        addresses: []
      };
    }
    return null;
  }

  componentDidMount() {
    this.mounted = true;
    document.addEventListener('mousedown', this.handleClickOutside, { capture: true });
  }

  componentDidUpdate(prevProps, prevState) {
    const { manualAddress, loqateAddress } = this.state;
    const { onChange, isManualAddress } = this.props;

    if (
      prevProps.query.value !== this.props.query.value &&
      this.props.query.value !== loqateLabel(loqateAddress) &&
      this.props.query.value
    ) {
      this.debouncedFindAddress();
    } else if (!this.props.query.value && loqateLabel(loqateAddress)) {
      this.handleResetAddress();
    }

    if (prevProps.isManualAddress !== isManualAddress) {
      if (isManualAddress) {
        onChange(fromElement(this.wrapperRef), {
          name: 'homeAddress',
          value: manualAddress
        });
      } else {
        onChange(fromElement(this.wrapperRef), {
          name: 'homeAddress',
          value: loqateAddress
        });
      }
    }

    if (!isEqual(prevState.loqateAddress, loqateAddress)) {
      onChange(fromElement(this.wrapperRef), {
        name: 'homeAddress',
        value: loqateAddress
      });
      if (loqateAddress) {
        const updates = [
          {
            name: 'query',
            value: loqateLabel(loqateAddress)
          },
          { name: 'address', value: loqateAddress.line1 },
          { name: 'address2', value: loqateAddress.line2 },
          { name: 'postal', value: loqateAddress.postcode },
          { name: 'city', value: loqateAddress.city }
        ];

        updates.forEach((prop) => {
          onChange(fromElement(this.wrapperRef), prop);
        });
      }
    }

    if (!isEqual(prevState.manualAddress, manualAddress)) {
      onChange(fromElement(this.wrapperRef), {
        name: 'homeAddress',
        value: manualAddress
      });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.debouncedFindAddress.cancel();
    document.removeEventListener('mousedown', this.handleClickOutside, { capture: true });
  }

  findAddress = async (containerId) => {
    try {
      const res = await this.props.findAddress(
        this.props.partialUserRef,
        this.props.query.value,
        containerId
      );
      if (this.mounted && res.data) {
        this.setState({
          loqateAddress: null,
          addresses: res.data?.addresses,
          serverError: null
        });
      }
    } catch (e) {
      if (e.msg && this.mounted) {
        this.setState({ serverError: e.msg });
      }
    }
  };

  // eslint-disable-next-line react/sort-comp -- can't with debounce
  debouncedFindAddress = debounce(this.findAddress, SEARCH_DEBOUNCE_MS);

  retrieveAddress = async (ev, id) => {
    this.setState({
      focusSearch: false
    });
    try {
      const res = await this.props.retrieveAddress(id, this.props.partialUserRef);
      if (this.mounted) {
        this.setState({ loqateAddress: res.data?.address, serverError: null, addresses: [] });
        this.props.resetAddressErrors();
      }
    } catch (e) {
      if (e.msg && this.mounted) {
        this.setState({ serverError: e.msg });
      }
    }
    this.handleToggleExpand();
  };

  handleClickOutside = (ev) => {
    const clickedOutside = this.wrapperRef && !this.wrapperRef.current.contains(ev.target);
    this.setState({ focusSearch: !clickedOutside });
    this.handleToggleExpand();
  };

  handleSelectAddress = (event) => {
    const value = event.currentTarget.getAttribute('data-__value');
    const address = find(this.state.addresses, { id: value });
    if (address.type !== 'Address') {
      this.findAddress(address.id);
    } else {
      this.retrieveAddress(event, address.id);
    }
    this.props.onAddressOptionSelected(address.type, `${address.text}, ${address.description}`);
  };

  handleResetAddress = () => {
    this.setState({ loqateAddress: null, addresses: [] });
    this.props.onChange(fromElement(this.wrapperRef), {
      name: 'query',
      value: null
    });
    this.elemRef.current.focus();
  };

  switchManually = () => {
    if (!this.props.query.value) {
      this.setState({ loqateAddress: null });
    }
    this.props.setAddressIsManual(!this.props.isManualAddress);
    this.handleToggleExpand();
  };

  handleToggleExpand = (queryText) => {
    const { query, isManualAddress, onAddressExpanded } = this.props;
    const { loqateAddress, focusSearch } = this.state;
    const loqateIsPopulated = !!(
      loqateAddress?.postcode ||
      loqateAddress?.city ||
      loqateAddress?.line1 ||
      loqateAddress?.line2
    );
    const queryLength = queryText?.length ?? query.value.length;
    const isExpanded = queryLength > 0 && focusSearch && !loqateIsPopulated && !isManualAddress;

    if (this.state.isExpanded !== isExpanded) {
      this.setState({ isExpanded });
      if (isExpanded && onAddressExpanded) {
        onAddressExpanded();
      }
    }
  };

  handleFocus = (e) => {
    this.setState({ focusSearch: true });
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
    this.handleToggleExpand();
  };

  handleAutoAddressChange = (e) => {
    this.props.onChange(e);
    this.handleToggleExpand(e.target.value);
  };

  render() {
    const {
      query,
      postal,
      city,
      country,
      address,
      address2,
      onFocus,
      onChange,
      loading,
      testId,
      hideAsterisk,
      showOptional,
      isManualAddress
    } = this.props;
    const { addresses, loqateAddress, serverError, isExpanded } = this.state;
    const loqateIsPopulated = !!(
      loqateAddress?.postcode ||
      loqateAddress?.city ||
      loqateAddress?.line1 ||
      loqateAddress?.line2
    );
    const queryField = {
      ...query,
      error: serverError,
      valid: loqateIsPopulated && query.value ? true : null
    };

    return (
      <div className="addressPicker">
        <div ref={this.wrapperRef}>
          {!isManualAddress ? (
            <Fieldset
              field={queryField}
              name="query"
              inputType="text"
              labelText={`${hideAsterisk ? '' : '* '}${
                query.value ? 'Address' : 'Type your address...'
              }${showOptional ? ' (optional)' : ''}`}
              autoComplete="falsy"
              onChange={this.handleAutoAddressChange}
              onFocus={this.handleFocus}
              ref={this.elemRef}
              icon={
                query.value ? (
                  <img
                    alt="Clear"
                    title="Clear address"
                    src="/assets/images1/grey-cross.svg"
                    onClick={this.handleResetAddress}
                  />
                ) : null
              }
              iconWidth={20}
              testId={testId}
            />
          ) : (
            <ManualAddress
              address={address}
              address2={address2}
              postal={postal}
              city={city}
              country={country}
              onChange={onChange}
              onFocus={onFocus}
            />
          )}

          {isExpanded && (
            <AddressList
              addresses={addresses}
              isLoading={loading}
              onSelectAddress={this.handleSelectAddress}
            />
          )}
        </div>

        <div className="addressPicker__manually">
          {isManualAddress ? (
            <a onClick={this.switchManually}>or find your address</a>
          ) : (
            <a onClick={this.switchManually}>or enter your address manually</a>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  loading: isLoading(state, Auth.AT.FIND_ADDRESS._),
  partialUserRef: Auth.selectors.getPartialUserRef(state)
});

const mapDispatchToProps = (dispatch) => ({
  findAddress: (partialUserRef, query, container) => {
    const addressOptions = container
      ? { partialUserRef, query, container }
      : { partialUserRef, query };
    return Api.actions.auth.findAddress(null, addressOptions)(dispatch, true);
  },
  retrieveAddress: (id, partialUserRef) =>
    Api.actions.auth.retrieveAddress(null, { id, partialUserRef })(dispatch, true)
});

_AddressPicker.propTypes = {
  query: PropTypes.object.isRequired,
  postal: PropTypes.object.isRequired,
  city: PropTypes.object.isRequired,
  country: PropTypes.object.isRequired,
  address: PropTypes.object.isRequired,
  address2: PropTypes.object.isRequired,
  partialUserRef: PropTypes.string,
  findAddress: PropTypes.func.isRequired,
  retrieveAddress: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  setAddressIsManual: PropTypes.func.isRequired,
  onAddressExpanded: PropTypes.func,
  onAddressOptionSelected: PropTypes.func,
  isManualAddress: PropTypes.bool,
  hideAsterisk: PropTypes.bool,
  showOptional: PropTypes.bool,
  testId: PropTypes.string
};

_AddressPicker.defaultProps = {
  isManualAddress: false,
  onAddressExpanded: null,
  onAddressOptionSelected: null,
  partialUserRef: null,
  onFocus: null,
  hideAsterisk: false,
  showOptional: false,
  testId: null
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLazyStyle([dropdownStyle, style])
)(_AddressPicker);
